/**
 * AdminEventCityHeatmap — admin-only heatmap of grounded-search query counts
 * grouped by event_type × city over the last N days.
 *
 * Each cell is colour-graded against the global max so the busiest
 * (event × city) intersections jump out instantly.  Row + column totals are
 * shown so admin can see which events / cities are pulling the most demand
 * even before reading individual cells.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChartScatter, Spinner } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DAY_OPTIONS = [7, 30, 90, 365];

/** Map 0..1 intensity to a brand-aligned hex tile. */
const tileBg = (count, max) => {
  if (!count || !max) return '#FAF7F2';   // sand-50 neutral
  const t = Math.min(1, count / max);
  // Brand burnt-terracotta (rgb 198, 90, 70). Lerp from sand-50 → terracotta.
  const r = Math.round(250 - (250 - 198) * t);
  const g = Math.round(247 - (247 - 90) * t);
  const b = Math.round(242 - (242 - 70) * t);
  return `rgb(${r}, ${g}, ${b})`;
};
const tileFg = (count, max) => {
  if (!count || !max) return '#7A6E5E';
  return count / max > 0.55 ? '#FFFFFF' : '#37281A';
};

const AdminEventCityHeatmap = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (d) => {
    setLoading(true);
    try {
      const { data: j } = await axios.get(
        `${BACKEND_URL}/api/admin/heatmaps/search-event-city`,
        { params: { days: d }, withCredentials: true }
      );
      setData(j);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load heatmap');
    }
    setLoading(false);
  };

  useEffect(() => { load(days); }, [days]);

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="admin-event-city-heatmap-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <ChartScatter size={20} className="text-brand-primary" weight="fill" />
          Search Demand · Event × City
        </CardTitle>
        <CardDescription>
          Where customer searches are landing. Each cell shows the count of grounded-search
          queries for that <strong>event type</strong> + <strong>city</strong> combination.
          Use this to spot underserved markets — busy cities for an event but few matching
          vendors are clear acquisition signals.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Window selector */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-[11px] uppercase tracking-wider font-bold text-text-secondary">
            Window:
          </span>
          {DAY_OPTIONS.map((d) => (
            <button key={d} onClick={() => setDays(d)}
              className={`text-xs px-3 py-1 border transition-colors ${
                days === d
                  ? 'border-brand-primary bg-brand-primary text-sand-50'
                  : 'border-[#E8E1D5] text-text-secondary hover:text-brand-primary'
              }`}
              data-testid={`heatmap-days-${d}`}>
              Last {d}d
            </button>
          ))}
          <span className="ml-auto text-[11px] text-text-secondary">
            {data ? `${data.total_searches} searches · max cell ${data.max_cell}` : ''}
          </span>
        </div>

        {loading ? (
          <div className="py-12 flex items-center justify-center text-text-secondary text-sm">
            <Spinner size={16} className="animate-spin mr-2" /> Crunching numbers…
          </div>
        ) : !data || data.rows.length === 0 ? (
          <div className="border border-[#E8E1D5] bg-sand-50 p-6 text-center"
            data-testid="heatmap-empty">
            <p className="text-sm text-text-primary mb-1">
              No customer searches yet in the last {days} days.
            </p>
            <p className="text-xs text-text-secondary">
              Once customers start using the AI Vendor Concierge on Instant Pricing the heatmap
              will populate here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="heatmap-grid-wrapper">
            <table className="w-full text-xs" data-testid="heatmap-grid">
              <thead>
                <tr>
                  <th className="text-left text-[10px] uppercase tracking-wider font-bold text-text-secondary pr-3 pb-2 align-bottom">
                    Event ↓ / City →
                  </th>
                  {data.cols.map((c) => (
                    <th key={c.city_slug}
                      className="px-1 pb-2 text-[10px] font-bold text-text-secondary"
                      title={`${c.city_label}: ${c.total} searches`}>
                      <div className="rotate-[-30deg] origin-bottom-left whitespace-nowrap pl-2">
                        {c.city_label}
                      </div>
                    </th>
                  ))}
                  <th className="text-[10px] uppercase tracking-wider font-bold text-text-secondary pl-3 pb-2 align-bottom">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.event_type} data-testid={`heatmap-row-${r.event_type}`}>
                    <td className="pr-3 py-1 text-sm font-medium text-text-primary whitespace-nowrap">
                      {r.event_label}
                    </td>
                    {r.cells.map((count, i) => (
                      <td key={i} className="p-0.5"
                        data-testid={`heatmap-cell-${r.event_type}-${data.cols[i].city_slug}`}>
                        <div className="h-8 min-w-[44px] flex items-center justify-center font-bold border border-[#E8E1D5]"
                          style={{ background: tileBg(count, data.max_cell), color: tileFg(count, data.max_cell) }}
                          title={`${r.event_label} · ${data.cols[i].city_label}: ${count} searches`}>
                          {count > 0 ? count : ''}
                        </div>
                      </td>
                    ))}
                    <td className="pl-3 py-1 text-sm font-bold text-brand-primary">
                      {r.total}
                    </td>
                  </tr>
                ))}
                {/* Column totals row */}
                <tr className="border-t border-[#E8E1D5]">
                  <td className="pr-3 py-1 text-[10px] uppercase tracking-wider font-bold text-text-secondary">
                    City total
                  </td>
                  {data.cols.map((c) => (
                    <td key={c.city_slug} className="p-0.5 text-center text-[11px] font-bold text-brand-primary">
                      {c.total}
                    </td>
                  ))}
                  <td className="pl-3 py-1 text-sm font-bold text-brand-primary">
                    {data.total_searches}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Colour-scale legend */}
            <div className="flex items-center gap-2 mt-4 text-[10px] text-text-secondary">
              <span>Less</span>
              {[0, 0.15, 0.35, 0.55, 0.8, 1].map((t) => (
                <div key={t} className="w-6 h-3 border border-[#E8E1D5]"
                  style={{ background: tileBg(t * (data.max_cell || 1), data.max_cell || 1) }} />
              ))}
              <span>More</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminEventCityHeatmap;
